import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import styles from "./Watchlist.module.css";
import WatchlistCard from "./WatchlistCard";
import MarketOverviewCards from "../MarketOverviewCards/MarketOverviewCards";
import { Pagination } from "@mui/material";
import SensorsIcon from "@mui/icons-material/Sensors";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import { getDefaultWatchlistPrice, getWatchlistPrice, updateWatchlistColors, removeWatchlistInstrument } from "../../api/watchlist";
import { buildMarketOverviewCards } from "../MarketOverviewCards/marketOverviewData";
import { buildWatchlistPriceItems, getWatchlistBookmarkColor } from "./watchlistPriceData";
import AddWatchlistSymbolModal from "./AddWatchlistSymbolModal";

const getWatchlistSymbolKey = (itemOrName) => {
  if (itemOrName && typeof itemOrName === "object") {
    if (itemOrName.tradingsymbol) return String(itemOrName.tradingsymbol).trim().toUpperCase();
    if (itemOrName.name) return String(itemOrName.name).split("/")[0].trim().toUpperCase();
    return "";
  }
  return String(itemOrName || "").split("/")[0].trim().toUpperCase();
};

const getRelativeTime = (date) => {
  if (!date) return "";
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds < 10) return "few seconds ago";
  if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
  if (diffInSeconds < 120) return "1 minute ago";
  return `${Math.floor(diffInSeconds / 60)} minutes ago`;
};

const POLL_INTERVAL_MS = 60 * 1000; // 1 minute

// Format seconds as MM:SS for countdown (time until next update)
const formatLiveTimer = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

function Watchlist() {
  const [activeTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 11;
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [, setRelativeTime] = useState("");
  const [liveCountdownSeconds, setLiveCountdownSeconds] = useState(60); // countdown until next poll (01:00 → 00:00)
  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [marketOverviewCards, setMarketOverviewCards] = useState([]);
  const [isMarketOverviewLoading, setIsMarketOverviewLoading] = useState(true);
  const [watchlistDataByTab, setWatchlistDataByTab] = useState({ ALL: [] });
  const [isWatchlistLoading, setIsWatchlistLoading] = useState(true);
  const [colorUpdatingId, setColorUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const pollingIntervalRef = useRef(null);
  const relativeTimeIntervalRef = useRef(null);

  const fetchWatchlistData = useCallback(async () => {
    try {
      const response = await getWatchlistPrice();
      const items = buildWatchlistPriceItems(response);
      setWatchlistDataByTab({
        ALL: items,
        "1": [],
        "2": [],
        "3": [],
      });
      return items;
    } catch (error) {
      console.error("Error fetching watchlist price data:", error);
    } finally {
      setIsWatchlistLoading(false);
    }
  }, []);

  const fetchDefaultWatchlistPrice = useCallback(async () => {
    try {
      const response = await getDefaultWatchlistPrice();
      setMarketOverviewCards(buildMarketOverviewCards(response));
    } catch (error) {
      console.error("Error fetching default watchlist price:", error);
    } finally {
      setIsMarketOverviewLoading(false);
    }
  }, []);

  const refreshWatchlistData = useCallback(async () => {
    await Promise.all([fetchWatchlistData(), fetchDefaultWatchlistPrice()]);
    setLastUpdateTime(new Date());
  }, [fetchDefaultWatchlistPrice, fetchWatchlistData]);

  // Countdown timer (01:00 → 00:00) and relative time; reset countdown on each update
  useEffect(() => {
    if (!lastUpdateTime) return;
    setLiveCountdownSeconds(60);
    const updateRelativeTime = () => setRelativeTime(getRelativeTime(lastUpdateTime));
    const tick = () => {
      setLiveCountdownSeconds((prev) => (prev > 0 ? prev - 1 : 0));
      updateRelativeTime();
    };
    updateRelativeTime();
    relativeTimeIntervalRef.current = setInterval(tick, 1000);
    return () => {
      if (relativeTimeIntervalRef.current) {
        clearInterval(relativeTimeIntervalRef.current);
        relativeTimeIntervalRef.current = null;
      }
    };
  }, [lastUpdateTime]);

  // Poll watchlist + market overview every 1 minute; cleared on unmount (leave dashboard).
  useEffect(() => {
    refreshWatchlistData();
    pollingIntervalRef.current = setInterval(() => {
      refreshWatchlistData();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (relativeTimeIntervalRef.current) {
        clearInterval(relativeTimeIntervalRef.current);
        relativeTimeIntervalRef.current = null;
      }
    };
  }, [refreshWatchlistData]);

  // Filter data based on search query (currentTabData inside useMemo for stable deps)
  const filteredData = useMemo(() => {
    const currentTabData = watchlistDataByTab[activeTab] || [];
    return currentTabData.filter((item) => {
      const query = searchQuery.toLowerCase();
      return (
        item.name?.toLowerCase().includes(query) ||
        item.tradingsymbol?.toLowerCase().includes(query) ||
        item.exchange?.toLowerCase().includes(query)
      );
    });
  }, [searchQuery, activeTab, watchlistDataByTab]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const existingWatchlistSymbolKeys = useMemo(() => {
    const keys = new Set();
    (watchlistDataByTab.ALL || []).forEach((item) => {
      const key = getWatchlistSymbolKey(item);
      if (key) keys.add(key);
    });
    return keys;
  }, [watchlistDataByTab.ALL]);

  // Reset to page 1 when search query changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleWatchlistColorChange = useCallback(
    async (item, colorIndex) => {
      if (!item?.exchangeTradingSymbol && !item?.tradingsymbol) return;

      const payload = [
        {
          exchange_trading_symbol: item.exchangeTradingSymbol || item.tradingsymbol,
          exchange: item.exchange,
          color: colorIndex,
        },
      ];

      const previousItems = watchlistDataByTab.ALL || [];
      setColorUpdatingId(item.id);
      setWatchlistDataByTab((prev) => ({
        ...prev,
        ALL: (prev.ALL || []).map((row) =>
          row.id === item.id
            ? {
                ...row,
                colorIndex,
                barColor: getWatchlistBookmarkColor(colorIndex),
              }
            : row
        ),
      }));

      try {
        await updateWatchlistColors(payload);
      } catch (error) {
        console.error("Error updating watchlist color:", error);
        setWatchlistDataByTab((prev) => ({ ...prev, ALL: previousItems }));
      } finally {
        setColorUpdatingId(null);
      }
    },
    [watchlistDataByTab.ALL]
  );

  const handleWatchlistDelete = useCallback(
    async (item) => {
      if (!item?.exchangeTradingSymbol && !item?.tradingsymbol) return;

      const payload = [
        {
          exchange_trading_symbol: item.exchangeTradingSymbol || item.tradingsymbol,
          exchange: item.exchange,
        },
      ];

      const previousItems = watchlistDataByTab.ALL || [];
      setDeletingId(item.id);
      setWatchlistDataByTab((prev) => ({
        ...prev,
        ALL: (prev.ALL || []).filter((row) => row.id !== item.id),
      }));

      try {
        await removeWatchlistInstrument(payload);
        setLastUpdateTime(new Date());
      } catch (error) {
        console.error("Error deleting watchlist item:", error);
        setWatchlistDataByTab((prev) => ({ ...prev, ALL: previousItems }));
      } finally {
        setDeletingId(null);
      }
    },
    [watchlistDataByTab.ALL]
  );

  return (
    <div className={styles["watchlist"]}>
      {/* First panel: two separate NIFTY 50 & SENSEX cards - combined height = metacard (129px) */}
      <MarketOverviewCards data={marketOverviewCards} isLoading={isMarketOverviewLoading} />

      {/* Second card: Watchlist (header, search, list) */}
      <div className={`${styles["watchlist-panel-card"]} ${styles["watchlist-panel-card--main"]}`}>
        <div className={styles["watchlist-header"]}>
          <div className={styles["watchlist-title-container"]}>
            <div className={styles["watchlist-icon"]}>
              <GlobalIcon iconName="cardLabelIcon" className={styles["watchlist-icon-svg"]} />
            </div>
            <h2 className={styles["watchlist-title"]}>Watchlist</h2>
          </div>
          <div className={styles["watchlist-live-row"]}>
            <span className={styles["watchlist-live-label"]}>
              <SensorsIcon className={styles["watchlist-live-icon"]} />
              Live update {formatLiveTimer(liveCountdownSeconds)}
            </span>
            <button
              type="button"
              className={styles["watchlist-refresh-btn"]}
              onClick={() => {
                setLiveCountdownSeconds(60);
                refreshWatchlistData();
                setIsRefreshSpinning(true);
                setTimeout(() => setIsRefreshSpinning(false), 600);
              }}
              title="Refresh"
            >
              <GlobalIcon
                iconName="RefreshIcon"
                className={`${styles["watchlist-refresh-icon"]} ${isRefreshSpinning ? styles["watchlist-refresh-icon--spin"] : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Search + Add / More */}
        <div className={styles["watchlist-search-row"]}>
          <div className={styles["search-input-wrapper"]}>
            <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles["search-input"]}
            />
          </div>
          <button
            type="button"
            className={styles["watchlist-add-btn"]}
            title="Add symbol"
            onClick={() => setIsAddModalOpen(true)}
          >
            <GlobalIcon iconName="AddIcon" className={styles["watchlist-add-icon"]} />
          </button>
          <button type="button" className={styles["watchlist-more-btn"]} title="More options">
            <MoreVertIcon className={styles["tab-icon-svg"]} />
          </button>
        </div>

        {/* List container */}
        <div className={styles["watchlist-content-container"]}>
          <div className={styles["watchlist-list"]}>
            {isWatchlistLoading && paginatedData.length === 0 ? (
              <div className={styles["watchlist-empty"]}>Loading watchlist...</div>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item) => (
                <WatchlistCard
                  key={item.id}
                  item={item}
                  onColorChange={handleWatchlistColorChange}
                  colorUpdatingId={colorUpdatingId}
                  onDelete={handleWatchlistDelete}
                  deletingId={deletingId}
                />
              ))
            ) : (
              <div className={styles["watchlist-empty"]}>No items found</div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {filteredData.length > itemsPerPage && (
          <div className={styles["watchlist-pagination"]}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              size="small"
              showFirstButton={false}
              showLastButton={false}
            />
          </div>
        )}
      </div>

      <AddWatchlistSymbolModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        existingSymbolKeys={existingWatchlistSymbolKeys}
        onAdded={() => refreshWatchlistData()}
        onRemoved={() => refreshWatchlistData()}
      />
    </div>
  );
}

export default Watchlist;
